/**
 * Log levels for the logger. Matches Python's logging levels for consistency.
 */
export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
  CRITICAL = 4,
}

/**
 * Logger utility class for consistent logging across the engine
 */
export class Logger {
  private static instance: Logger;
  private currentLevel: LogLevel;
  private frameNumber: number;
  private logLevels: LogLevel[];
  private currentLevelIndex: number;

  private constructor() {
    this.currentLevel = LogLevel.INFO;
    this.frameNumber = 0;
    this.logLevels = [
      LogLevel.INFO,
      LogLevel.DEBUG,
      LogLevel.WARN,
      LogLevel.ERROR,
      LogLevel.CRITICAL,
    ];
    this.currentLevelIndex = 1; // INFO by default
  }

  public static getInstance(): Logger {
    if (!Logger.instance) {
      Logger.instance = new Logger();
    }
    return Logger.instance;
  }

  public debug(message: string): void {
    if (this.currentLevel <= LogLevel.DEBUG) {
      console.debug(`[Frame ${this.frameNumber}] [DEBUG] ${message}`);
    }
  }

  public info(message: string): void {
    if (this.currentLevel <= LogLevel.INFO) {
      console.info(`[Frame ${this.frameNumber}] [INFO] ${message}`);
    }
  }

  public warn(message: string): void {
    if (this.currentLevel <= LogLevel.WARN) {
      console.warn(`[Frame ${this.frameNumber}] [WARN] ${message}`);
    }
  }

  public error(message: string): void {
    if (this.currentLevel <= LogLevel.ERROR) {
      console.error(`[Frame ${this.frameNumber}] [ERROR] ${message}`);
    }
  }

  public critical(message: string): void {
    if (this.currentLevel <= LogLevel.CRITICAL) {
      console.error(`[Frame ${this.frameNumber}] [CRITICAL] ${message}`);
    }
  }

  public setLevel(level: LogLevel): void {
    this.currentLevel = level;
  }

  public cycleLogLevel(): void {
    this.currentLevelIndex =
      (this.currentLevelIndex + 1) % this.logLevels.length;
    this.currentLevel = this.logLevels[this.currentLevelIndex];
  }

  public incrementFrame(): void {
    this.frameNumber++;
  }

  public getFrameNumber(): number {
    return this.frameNumber;
  }

  public getCurrentLevel(): LogLevel {
    return this.currentLevel;
  }
}
